/**
 * StripeCancelPage — Pagina post-annullamento pagamento Stripe.
 * Route: /ordine-annullato (pubblica)
 *
 * Stripe redireziona qui se l'utente annulla il checkout.
 * Nessun ordine viene creato fino al pagamento, quindi qui rassicuriamo l'utente.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  XCircle, ShieldCheck, RefreshCw, ArrowLeft,
  ShieldOff, Lock, HelpCircle, MessageCircle, ArrowRight,
} from 'lucide-react';
import { API_ENDPOINTS } from '../../Config/api';

const REASSURANCE = [
  {
    icon: ShieldOff,
    title: 'Nessun addebito',
    desc: 'Non è stato effettuato alcun addebito sulla tua carta.',
    color: 'green',
  },
  {
    icon: Lock,
    title: 'Dati al sicuro',
    desc: 'I tuoi dati personali non sono stati memorizzati.',
    color: 'blue',
  },
];

const FAQ = [
  {
    q: 'Posso riprovare il pagamento?',
    a: 'Sì, puoi tornare allo store e completare l\'ordine in qualsiasi momento.',
  },
  {
    q: 'Ho bisogno di assistenza?',
    a: null, // special: renders mailto link
  },
];

const colorMap = {
  green: { bg: 'bg-green-50', border: 'border-green-100', icon: 'text-green-600', title: 'text-green-800', desc: 'text-green-700' },
  blue:  { bg: 'bg-blue-50',  border: 'border-blue-100',  icon: 'text-blue-600',  title: 'text-blue-800',  desc: 'text-blue-700'  },
};

export default function StripeCancelPage() {
  const navigate = useNavigate();
  const ref = localStorage.getItem('asight_channel') || '';

  const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const defaultLogoUrl = `${apiBaseUrl}/api/auth/media/branding/a-sight_LOGO_DEF.png`;
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(null);

  const withRef = (path) => {
    if (ref) {
      const sep = path.includes('?') ? '&' : '?';
      return `${path}${sep}ref=${encodeURIComponent(ref)}`;
    }
    return path;
  };

  useEffect(() => {
    const url = ref
        ? `${API_ENDPOINTS.brandingLogo}?ref=${encodeURIComponent(ref)}`
        : API_ENDPOINTS.brandingLogo;
    fetch(url)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
            setBrandingLogoUrl(data?.logo_url || defaultLogoUrl);
        })
        .catch(() => { setBrandingLogoUrl(defaultLogoUrl); });
  }, [ref]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-white via-yellow-100 to-yellow-300 border-b border-yellow-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center justify-center" style={{ minHeight: 80 }}>
          {brandingLogoUrl && <img src={brandingLogoUrl} alt="Logo" draggable="false" style={{ maxHeight: 80, userSelect: 'none' }} />}
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-12 flex-1 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl border border-amber-100 p-8 md:p-10 relative overflow-hidden"
        >
          {/* Amber accent bar */}
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500" />

          {/* Icon */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ scale: 0, rotate: -90 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.2 }}
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 flex items-center justify-center border-2 border-amber-200">
              <XCircle className="w-12 h-12 text-amber-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center">
              Pagamento non completato
            </h2>
            <p className="text-gray-500 text-center mb-8 max-w-md mx-auto">
              Il processo di pagamento è stato interrotto. Non preoccuparti, puoi riprovare quando vuoi.
            </p>
          </motion.div>

          {/* Reassurance cards */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto mb-8"
          >
            {REASSURANCE.map((item, i) => {
              const Icon = item.icon;
              const c = colorMap[item.color];
              return (
                <motion.div
                  key={i}
                  whileHover={{ y: -2 }}
                  className={`flex items-start gap-3 ${c.bg} border ${c.border} rounded-xl p-4 transition-shadow hover:shadow-md`}
                >
                  <div className={`p-2 rounded-lg ${c.bg} flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${c.icon}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-bold ${c.title}`}>{item.title}</p>
                    <p className={`text-xs ${c.desc} mt-0.5`}>{item.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-5 max-w-lg mx-auto mb-8 border border-gray-100"
          >
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-bold text-gray-700">Domande frequenti</h3>
            </div>
            <div className="space-y-4 text-sm">
              {FAQ.map((item, i) => (
                <div key={i}>
                  <p className="font-semibold text-gray-700">{item.q}</p>
                  {item.a ? (
                    <p className="text-gray-500 mt-0.5">{item.a}</p>
                  ) : (
                    <p className="text-gray-500 mt-0.5">
                      Contattaci a{' '}
                      <a
                        href="mailto:support@a-sight.it"
                        className="inline-flex items-center gap-1 text-yellow-600 hover:text-yellow-700 hover:underline font-medium"
                      >
                        <MessageCircle className="w-3 h-3" />
                        support@a-sight.it
                      </a>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <button
              onClick={() => navigate(withRef('/store/ordine'))}
              className="group flex items-center gap-2 px-7 py-3 text-sm font-bold text-white bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl hover:from-yellow-600 hover:to-amber-600 transition-all shadow-md shadow-yellow-200/50 hover:shadow-lg"
            >
              <RefreshCw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" />
              Riprova l'ordine
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate(withRef('/'))}
              className="group flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              Torna alla home
            </button>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-yellow-200 bg-gradient-to-r from-white via-yellow-50 to-yellow-100">
        <div className="max-w-3xl mx-auto px-4 py-4 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} A-Sight — Innova-ware. Tutti i diritti riservati.
        </div>
      </footer>
    </div>
  );
}
